import {ChangeDetectionStrategy, Component, computed, effect, ElementRef, Injectable, input, linkedSignal, OnDestroy, output, signal, viewChild} from "@angular/core";
import {MøteId, OppdaterSaksrekkefølgeSpesifikasjon, SakSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {RadHandling, SakListevisningKomponent} from "../sak-listevisning/sak-listevisning";
import {sakVarighetSomDuration} from "felles-ng2/modell/sakModellHjelpere";
import {CdkDrag, CdkDragDrop, CdkDragPlaceholder, CdkDragPreview, CdkDragStart, CdkDropList, DragRef, moveItemInArray, Point,} from '@angular/cdk/drag-drop';
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {erFlerdagsmøte, erRedigerMøteMulig, lesMøtedato2} from "felles-ng2/modell/møteModellHjelpere";
import {Temporal} from "temporal-polyfill";
import {durationTilMinutter, plainTimeFraZonedDateTime, plainTimeFraZonedDateTimeUTC, skrivPlainTimeSerialisert, skrivZonedDateTimeSerialisert} from "felles-ng2/støttefunksjoner/temporal";
import {GjeldendeTidssone, Tidssoneinformasjon} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erIkkeTomListe, erTomListe, første, innslagPåIndeks, reduserTilSum} from "felles-ng2/støttefunksjoner/lister";
import {ZonedDateTimeSomPlainDatePipe} from "felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe";
import {FormArray, FormControl, ReactiveFormsModule} from "@angular/forms";
import {KlokkeslettVelgerKomponent} from "web-ng2/delt/komponenter/klokkeslett-velger/klokkeslett-velger";
import {Subscription} from "rxjs";
import {NavigasjonDeaktivering} from "web-ng2/tjenester/NavigasjonDeaktivering";
import {NavigasjonforhindretFlashDirektiv} from "web-ng2/delt/direktiver/navigasjonforhindret-flash/navigasjonforhindret-flash";
import {SaksinfoForSakstidspunktberegning, Sakstidspunkt, Sakstidspunkter, SakstidspunkterBeregning} from "felles-ng2/tjenester/Sakstidspunkter";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {InlineMeldingKomponent} from "web-ng2/delt/komponenter/inline-melding/inline-melding";
import {MatTooltip} from "@angular/material/tooltip";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {ValgtSakRegister} from "web-ng2/tjenester/ValgtSakRegister";
import {AgendaelementModellDLM} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import ZonedDateTime = Temporal.ZonedDateTime;
import Duration = Temporal.Duration;
import PlainTime = Temporal.PlainTime;


const debug = initDebugger('SakslisteKomponent');

export type SaksvarighetKontroll = FormControl<Duration | null>;
export type DagStarttidspunktKontroll = FormControl<PlainTime | null>;

/**
 * Lister saker for et møte.
 */
@Component({
    selector: 'saksliste',
    templateUrl: 'saksliste.html',
    styleUrl: 'saksliste.scss',
    host: {
        'data-testid': 'saksliste',
        '[class.kompakt]': 'visKompakt()',
        '[class.flerdags]': 'erFlerdagsmoete()'
    },
    imports: [
        SakListevisningKomponent,
        CdkDropList,
        CdkDrag,
        CdkDragPreview,
        CdkDragPlaceholder,
        MatButton,
        TranslateModule,
        FormaterPlainDatePipe,
        ZonedDateTimeSomPlainDatePipe,
        KlokkeslettVelgerKomponent,
        ReactiveFormsModule,
        NavigasjonforhindretFlashDirektiv,
        InlineMeldingKomponent,
        MatTooltip
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakslisteKomponent implements OnDestroy {

    /** Antall piksler .sak-preview-kontainer skal forskyves til høyre når bruker flytte sak vhja dra-og-slipp */
    static sakPreviewKontainerHorisontaltOffset = 20;

    private sakerKontainer = viewChild.required<ElementRef<HTMLElement>>('sakerKontainer');

    vedSorteringAktivert = output<boolean>();

    saker = input.required<AgendaelementModellDLM[]>();
    /** Aktiverer kompakt visning - da vises kun saksnummer, sakstittel og klokkeslett/varighet.
     * For bruk når saksdetaljer-visning er aktivert i sak-liste-og-detaljer */
    kompakt = input(false);
    /** Aktiverer ekstra kompakt visning - da vises kun saksnummer og klokkeslett/varighet.
     * For bruk når bla presentasjonsmodus er aktivert  */
    ekstraKompakt = input(false);

    rader = signal<Rad[]>([]);
    redigeringMulig = computed(() => this.beregnRedigeringMulig());
    sorteringEndret = computed(() => this.erSorteringEndret());
    sorteringAktivert = signal(false);
    visKompakt = computed(() => this.kompakt() && !this.sorteringAktivert());
    erFlerdagsmoete = computed(() => erIkkeTomListe(this.saker()) ? erFlerdagsmøte(this.saker()[0].moete) : false);
    antallSakerSomManglerVarighet = signal(0);
    lagrer = asynkArbeidPågårSignal();

    skjema = new FormArray<DagStarttidspunktKontroll|SaksvarighetKontroll>([]);

    /** Samlingen av rader slik den ser ut initielt før bruker har gjort eventuelle endringer i rekkefølge/varighet/starttidspunkt */
    private raderUendret = <Rad[]>[];
    private sakstidspunktberegninger: SakstidspunkterBeregning | null = null; //settes av somRader()

    constructor(private avhengigheter: SakslisteKomponentAvhengigheter,
                private sakstidspunkter: Sakstidspunkter,
                private gjeldendeTidssone: GjeldendeTidssone,
                private navigasjonDeaktivering: NavigasjonDeaktivering) {

        //set verdier som er avledet av saker (kan ikke definere rader som linkedSignal da initSkjemaOgReturnerSomRader() leser rader...
        toObservable(this.saker).pipe(takeUntilDestroyed()).subscribe(saker => {
            this.rader.set(this.initSkjemaOgReturnerSomRader(saker))
            this.raderUendret = [...this.rader()];
        });

        //logikk knyttet til at sortering aktiveres/deaktiveres
        effect(() => {
            this.vedSorteringAktivert.emit(this.sorteringAktivert());
            this.navigasjonDeaktivering.aktiverNavigasjon(!this.sorteringAktivert());
        });

        //reberegn antallSakerSomManglerVarighet når skjema oppdateres
        this.skjema.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(() => this.beregnAntallSakerSomManglerVarighet());
    }

    vedHandling(handling: RadHandling, rad: Rad, indeks: number) {
        switch (handling) {
            case RadHandling.flyttOpp:
                return this.flyttRad(indeks, indeks - 1);
            case RadHandling.flyttNed:
                return this.flyttRad(indeks, indeks + 1);
        }
    }

    /** Returnerer funk som beregner posisjonen der .sak-placeholder-kontainer skal vises når sak dras ifbm dra-og-slipp sortering. */
    sakPreviewKontainerPlasseringFn() {
        return this.beregnPreviewKontainerPlasseringFn(SakslisteKomponent.sakPreviewKontainerHorisontaltOffset, -70); //y-70 for å aligne musemarkør med .sakssortering-kontainer
    }

    /** Returnerer funk som beregner posisjonen der .dag  skal vises når sak dras ifbm dra-og-slipp sortering. */
    dagPreviewKontainerPlasseringFn() {
        return this.beregnPreviewKontainerPlasseringFn(SakslisteKomponent.sakPreviewKontainerHorisontaltOffset, -20); //y-20 for å aligne musemarkør med vertikal midt av .dag
    }

    /** Aktiverer saks-sortering, eksponert for at omverden skal kunne aktivere sorteringsmoduset */
    aktiverSortering(){
        this.sorteringAktivert.set(true);
    }

    /** Kalles når dra-og-slipp initialiseres for en saks-rad.
     * Setter høyden på placeholder-elementet (.sak-placeholder-kontainer) når en saks-rad flyttes.
     * Nødvendig da .sak-placeholder-kontainer er et tomt element og ellers ikke opptar noen høyde.
     * Setter bredden på .sak-preview-kontainer da denne ellers vil flyte ut over sakslistens bredde. */
    flyttingAvSakStartet({source}: CdkDragStart<SakRad>) {
        const listeElement = source.element.nativeElement;
        //vi forutsetter at .nummer (som repr høyden på .tittel-linje) og .tid (som repr høyden på .detaljer-linje) alltid eksisterer
        const radHøyde = Array.from(listeElement.querySelectorAll<HTMLElement>('.nummer,.tid'))
          .map(element => element.offsetHeight)
          .reduce(...reduserTilSum());
        source.getPlaceholderElement().style.height = `${radHøyde}px`;

        //set bredden på .sak-preview-kontainer
        const sakerKontainerDimensjon = this.sakerKontainer().nativeElement.getBoundingClientRect()
        const previewKontainer = this.sakerKontainer().nativeElement.querySelector('.sak-preview-kontainer')! as HTMLElement;
        previewKontainer.style.width = `${sakerKontainerDimensjon.width - SakslisteKomponent.sakPreviewKontainerHorisontaltOffset}px`;
    }

    /** Kalles når dra-og-slipp initialiseres for en dag-rad.
     * Setter høyden på placeholder-elementet (.dag-placeholder-kontainer) når en dag-rad flyttes.
     * Nødvendig da .dag-placeholder-kontainer er et tomt element og ellers ikke opptar noen høyde.*/
    flyttingAvDagStartet({source}: CdkDragStart<DagRad>) {
        const listeElement = source.element.nativeElement;
        const radHøyde = listeElement.offsetHeight;
        source.getPlaceholderElement().style.height = `${radHøyde}px`;
    }

    flyttingTillatFn() {
        return (indeks: number, flyttetRad: CdkDrag<Rad>) => this.erFlyttingTillat(indeks, flyttetRad.data);
    }

    /** Når bruke har datt en sak og sluppet den på ny plassering */
    radFlyttet(event: CdkDragDrop<any, any, Rad>) {
        const data = event.item.data;
        debug(`flytter ${data.erSak() ? 'sak' : 'dag'} far ${event.previousIndex} til ${event.currentIndex}`, event);
        this.flyttRad(event.previousIndex, event.currentIndex);
    }

    async lagreNySortering() {
        await this.lagrer.utfør(async () => {
            await this.avhengigheter.oppdaterSaksrekkefølge(this.møteId(), this.somSpesifikasjon());
            this.sakstidspunktberegninger?.resetOverstyringer();
            this.sorteringAktivert.set(false);
        });
    }

    avbrytNySortering() {
        this.rader.set(this.raderUendret);
        this.rader().forEach(rad => rad.reset());
        this.sorteringAktivert.set(false);
        this.sakstidspunktberegninger?.resetOverstyringer();
    }

    ngOnDestroy(): void {
        this.terminerEndringsAbonnementer();
        this.sakstidspunktberegninger?.terminer();
    }

    //private hjelpemetoder

    /** Returnerer funksjon som beregner plasseringen til previewKontainer ved flytting av sak/dag
     * Merk at vi tillater kun vertikal bevegelse. */
    private beregnPreviewKontainerPlasseringFn(xOffset: number, yOffset: number): (punkt: Point, dragRef: DragRef) => Point {
        let initieltXOffset = -1;
        return (punkt: Point, dragRef: DragRef): Point => {
            //initielt x-offset leses kun ved start av flytting for å unngå unødvendig mange kall til getBoundingClientRect()
            if (initieltXOffset < 0) initieltXOffset = this.sakerKontainer().nativeElement.getBoundingClientRect().x;
            return {x: initieltXOffset + xOffset, y: punkt.y + yOffset};
        }
    }

    private beregnRedigeringMulig(){
        // kaller alle signaler først, så de blir recorda, dette kalles i en computed()
        const sorteringAktivert = this.sorteringAktivert();
        const navigasjonDeaktivert = this.navigasjonDeaktivering.navigasjonDeaktivertSomSignal();
        const saker = this.saker();
        if (sorteringAktivert) return true;
        if (navigasjonDeaktivert) return false;
        return erIkkeTomListe(saker) ?
          this.avhengigheter.redigerSakerMulig(saker[0].moete.id) :
          false;
    }

    private erSorteringEndret() {
        const initiellSortering = this.raderUendret.map(rad => rad.id);
        const nySortering = this.rader().map(rad => rad.id);
        return nySortering.toString() != initiellSortering.toString();
    }

    /** Returnerer Sak/DagRad representasjon av alle agendaelement-innslag i moete.saker, samt populerer skjema med disse */
    private initSkjemaOgReturnerSomRader(saker: AgendaelementModellDLM[]): Rad[] {
        this.skjema.clear();
        if (erTomListe(this.saker())) return [];

        //init sakstidspunktberegninger kun én gang
        if (erNil(this.sakstidspunktberegninger)) this.sakstidspunktberegninger = this.sakstidspunkter.forMøte(this.møteId());
        const møtedato = this.møtedato()
        const tidspunkter = this.sakstidspunktberegninger.forSaker(saker);
        if (this.erFlerdagsmoete()) {
            //for hver dag, legg til i listen et 'dag' element etterfulgt av alle sakene knyttet til denne dagen
            let indeks = 0;
            return [...Array(møtedato.antallDager())]
              .flatMap((_, dagNummer) => {
                  const sakerPåDenneDag = saker.filter(sak => (sak.dag ?? 0) == dagNummer);
                  let startdato = møtedato.start.add({days: dagNummer});
                  const førsteSak = første(sakerPåDenneDag);
                  //ikke gitt at aller dager har saker - derfor null-sjekk
                  //merk at vi ved flytting av saker mellom dager midlertidig kan være i en tilstand der sak.start ikke er satt for første sak på dag
                  if (erIkkeNil(førsteSak)) startdato = tidspunkter.forSak(førsteSak.id)!.start! ?? startdato;
                  return [
                      //dag innslag
                      this.somDagRad(indeks++, dagNummer, startdato),
                      //alle sakene på denne dagen
                      ...sakerPåDenneDag.map(sak => this.somSakRad(indeks++, sak, tidspunkter.forSak(sak.id)!))
                  ]
              });
        } else
            return saker.map((sak, indeks) => this.somSakRad(indeks, sak, tidspunkter.forSak(sak.id)!));
    }

    private møtedato() {
        return lesMøtedato2(første(this.saker())!.moete, this.gjeldendeTidssone.brukersTidssone());
    }

    private møteId() {
        return første(this.saker())!.moete.id;
    }

    private somSakRad(indeks: number, sak: AgendaelementModellDLM, tidspunkt: Sakstidspunkt): SakRad {
        const saksRad = new SakRad(indeks, sak, tidspunkt, () => this.reberegnTidspunkter(this.rader()));
        this.skjema.push(saksRad.sak.varighetKontroll);
        return saksRad;
    }

    private somDagRad(indeks: number, dagNummer0Basert: number, datoOgTid: ZonedDateTime): DagRad {
        var dagRad = new DagRad(indeks, dagNummer0Basert, datoOgTid, this.tidssone(), () => this.reberegnTidspunkter(this.rader()));
        this.skjema.push(dagRad.dag.startTidspunktKontroll);
        return dagRad;
    }

    private beregnAntallSakerSomManglerVarighet() {
        this.antallSakerSomManglerVarighet.set(this.rader()
          .filter(rad => rad.erSak())
          .filter(saksrad => erNil(saksrad.sak.varighetKontroll.value))
          .length);
    }

    private terminerEndringsAbonnementer() {
        this.rader().forEach(rad => rad.terminerEndringsAbonnement());
    }

    private tidssone(): Tidssoneinformasjon {
        return this.gjeldendeTidssone.brukersTidssone();
    }

    /** Reberegner saksinfo for alle saker i angitt liste.
     * Muterer SakRad.sak.saksinfo og SakRad.sak.tidspunkt */
    private reberegnTidspunkter(rader: Rad[]): void {
        //saniter sak.starttidspunkt/sak.dag, må gjøres dersom
        // - dag.dato (klokkeslett-del) har blitt endret
        // - dag har blitt flyttet
        // - første sak innenfor en dag har blitt endret
        let aktivDag: DagRad | null = null;
        rader.forEach((rad, indeks) => {
            //oppdater av sak.dag
            if (rad.erDag()) aktivDag = rad;
            if (rad.erSak() && erIkkeNil(aktivDag)) rad.sak.saksinfo.dag = aktivDag.dag.dagNummer;

            //oppdater sak.starttidspunkt
            const nesteRad = innslagPåIndeks(rader, indeks + 1);
            if (rad.erDag() && nesteRad?.erSak())
                nesteRad.sak.saksinfo.starttidspunkt = skrivZonedDateTimeSerialisert(rad.dag.dato);
        });

        //oppdater rad.sak.tidspunkt for alle saksrader, merk at endringer i sak.varighetKontroll vil ha blitt kopiert til sak.saksinfo.varighet på dette tidspunktet
        var saksrader = rader.filter(rad => rad.erSak());
        const tidspunkter = this.sakstidspunktberegninger!.medOverstyringerForAlleSaker(saksrader.map(rad => rad.sak.saksinfo));
        saksrader.forEach(saksrad => saksrad.sak.tidspunkt = tidspunkter.forSak(saksrad.id))
    }

    /** sann dersom angitt rad kan flyttes til angitt indeks */
    //public for testbarhet
    erFlyttingTillat(indeks: number, flyttetRad: Rad) {
        debug(`flyttingTillat indeks:${indeks}, flyttetRad.id:${flyttetRad.id}`, flyttetRad)
        if (!this.erFlerdagsmoete) return true; //alltid lov å flytte i et enkeltdagsmøte

        if (flyttetRad.erSak()) {
            //ved flytting av sak
            //sak kan ikke flyttes før første dag-innslag (som alltid har indeks=0)
            return indeks > 0;

        } else {
            //ved flytting av dag
            //kan ikke endre rekkefølgen på dagene (flytte en dag forbi en annen dag)
            //ligger det noen dager i intervallet mellom flyttetRad opprinnelig indeks og ny indeks? - i så fall kan vi ikke flytte
            const opprinneligIndeks = this.rader().indexOf(flyttetRad)
            return !this.rader().slice(Math.min(opprinneligIndeks, indeks), Math.max(opprinneligIndeks, indeks))
              .some(rad => rad.erDag() && rad.id != flyttetRad.id);
        }
    }

    //public for testbarhet
    flyttRad(fraIndeks: number, tilIndeks: number) {
        //ved RadHandling.flyttOpp/RadHandling.flyttNed kan tilIndeks være utenfor grensene
        if (tilIndeks < 0 || tilIndeks > this.rader().length - 1) return;

        const kopiAvRader = [...this.rader()];
        moveItemInArray(kopiAvRader, fraIndeks, tilIndeks);
        kopiAvRader[tilIndeks].erFlyttet = kopiAvRader[tilIndeks].initiellIndeks != tilIndeks
        this.reberegnTidspunkter(kopiAvRader);

        this.rader.set(kopiAvRader);
        this.sorteringAktivert.set(this.sorteringEndret());
    }

    //public for testbarhet

    somSpesifikasjon(): OppdaterSaksrekkefølgeSpesifikasjon {
        const saker = this.rader().filter(rad => rad.erSak()).map(rad => rad.somSpesifikasjon());
        return {
            saker: saker
        } as OppdaterSaksrekkefølgeSpesifikasjon;
    }

}


type Rad = SakRad | DagRad;

abstract class BasisRad {
    erFlyttet = false;

    constructor(
      /** id som benyttes i for track i @for i gui */
      public readonly id: string,
      /** hvorvidt denne raden representerer et agendaelement (true) eller en dag (false) */
      private readonly erSaksrad: boolean,
      /** initiell plassering i rader-listen */
      public readonly initiellIndeks: number) {
    }

    erSak(): this is SakRad {
        return this.erSaksrad;
    }

    erDag(): this is DagRad {
        return !this.erSaksrad;
    }

    reset(): void {
        this.erFlyttet = false;
    }

    abstract terminerEndringsAbonnement(): void;
}

class SakRad extends BasisRad {
    // indeks: number; //plassering i rader-samlingen
    readonly sak: {
        readonly sak: AgendaelementModellDLM;
        /** muterbar kopi av saksinformasjon nødvendig for å beregne Sakstidspunkt vhja {@link SakstidspunkterBeregning} */
        saksinfo: SaksinfoForSakstidspunktberegning;
        tidspunkt: Sakstidspunkt;
        readonly varighetKontroll: SaksvarighetKontroll;
    };
    readonly dag: null;

    private readonly initieltTidspunkt: Sakstidspunkt;
    private endringAbonnement!: Subscription;

    constructor(initiellIndeks: number, sak: AgendaelementModellDLM, tidspunkt: Sakstidspunkt, private reberegnTidspunkter: () => void) {
        super(sak.id, true, initiellIndeks)
        this.sak = {
            sak: sak,
            saksinfo: this.saksinfoFraSak(sak),
            tidspunkt: tidspunkt,
            varighetKontroll: new FormControl(sakVarighetSomDuration(sak))
        };
        this.dag = null;
        this.initieltTidspunkt = tidspunkt;

        this.lyttTilEndringIVarighet();
    }

    somSpesifikasjon(): SakSpesifikasjon {
        return {
            id: this.sak.sak.id,
            dag: this.sak.tidspunkt.dag,
            varighet: durationTilMinutter(this.sak.varighetKontroll.value),
            starttidspunkt: erIkkeNil(this.sak.tidspunkt.start) ? skrivPlainTimeSerialisert(plainTimeFraZonedDateTimeUTC(this.sak.tidspunkt.start)) : null
        };
    }

    reset() {
        super.reset();
        this.sak.tidspunkt = this.initieltTidspunkt;
        this.sak.saksinfo = this.saksinfoFraSak(this.sak.sak);
    }

    terminerEndringsAbonnement() {
        this.endringAbonnement.unsubscribe();
    }

    private saksinfoFraSak(sak: AgendaelementModellDLM): SaksinfoForSakstidspunktberegning {
        return {id: sak.id, dag: sak.dag, starttidspunkt: sak.starttidspunkt, varighet: sak.varighet};
    }

    private lyttTilEndringIVarighet() {
        this.endringAbonnement = this.sak.varighetKontroll.valueChanges.subscribe(nyVarighet => {
            debug(`ny varighet ${nyVarighet} angitt`)
            this.sak.saksinfo.varighet = durationTilMinutter(nyVarighet);
            //reberegn sakstidspunkter
            this.reberegnTidspunkter();
        });

    }
}

class DagRad extends BasisRad {

    readonly dag: {
        /** 0-basert dagnummer */
        readonly dagNummer: number;
        dato: ZonedDateTime;
        readonly startTidspunktKontroll: DagStarttidspunktKontroll;
    };
    readonly sak: null;

    private readonly initiellDato: ZonedDateTime;
    private endringAbonnement!: Subscription;

    constructor(initiellIndeks: number, dagNummer0Basert: number, datoOgTid: ZonedDateTime, private tidssone: Tidssoneinformasjon, private reberegnTidspunkter: () => void) {
        super(datoOgTid.toString(), false, initiellIndeks);
        this.dag = {
            dagNummer: dagNummer0Basert,
            dato: datoOgTid,
            startTidspunktKontroll: new FormControl(plainTimeFraZonedDateTime(datoOgTid, tidssone), {nonNullable: true})
        };
        this.sak = null;
        this.initiellDato = datoOgTid;

        if (dagNummer0Basert == 0) this.dag.startTidspunktKontroll.disable(); //endring av starttidspunkt for første dag må gjøres ved å redigere møte
        this.lyttTilEndringIStarttidspunkt();
    }

    reset() {
        super.reset();
        this.dag.dato = this.initiellDato;
    }

    terminerEndringsAbonnement() {
        this.endringAbonnement.unsubscribe();
    }

    private lyttTilEndringIStarttidspunkt() {
        this.endringAbonnement = this.dag.startTidspunktKontroll.valueChanges.subscribe(nyttStarttidspunkt => {
            debug(`nytt starttidspunkt ${nyttStarttidspunkt} angitt`)
            if (erNil(nyttStarttidspunkt)) return; //null blir levert når angitt klokkeslett ikke er gyldig
            this.dag.dato = this.dag.dato.withTimeZone(this.tidssone.somZoneId()).withPlainTime(nyttStarttidspunkt);
            //reberegn sakstidspunkter
            this.reberegnTidspunkter();
        });
    }
}

@Injectable()
export class SakslisteKomponentAvhengigheter {

    constructor(private møteDatatjeneste: MøteDatatjeneste, private kontekst:BrukerkontekstTilbyder) {
    }

    redigerSakerMulig(møteId: MøteId): boolean {
        return erRedigerMøteMulig(
          this.møteDatatjeneste.hentFraFraLokallager(møteId),
          this.kontekst.autentisertBruker());
    }

    async oppdaterSaksrekkefølge(id: MøteId, spesifikasjon: OppdaterSaksrekkefølgeSpesifikasjon): Promise<void> {
        await this.møteDatatjeneste.oppdaterSaksrekkefølge(id, spesifikasjon);
    }
}
